import { useFormContext } from 'react-hook-form';

import Box from '~/common/components/Box';
import GenericTooltip from '~/common/components/GenericTooltip';
import RadioComponent from '~/common/components/Radios/Radio';
import RadioGroup from '~/common/components/Radios/RadioGroup';
import Text from '~/common/components/Texts/Text';

import HelperText from '../../components/HelperText';
import ConditionEvaluator from '../../components/utilityComponents/ConditionEvaluator';
import StandardFormControl from '../../components/utilityComponents/StandardFormControl';
import { useQuestionLibraryActions } from '../../store/questionLibraryStore';
import type { ElementConditions } from '../../types/common';
import type { SmartAnswer } from '../../types/smartAnswers';
import type { QuestionLibraryError } from '../../utils/errorHandling';

type SimpleRadioProps = {
  field?: any;
  name?: string;
  label?: string;
  rules?: any;
  options?: any[];
  infoToolTip?: string;
  error?: QuestionLibraryError;
  smartAnswer?: SmartAnswer;
  row?: boolean;
  additionalDisplayText?: {
    text: string;
    conditions?: ElementConditions;
  };
  radioGroupPaddingTop?: string;
  optionMarginRight?: string;
};

const getRadioGroupPaddingTop = (
  radioGroupPaddingTop: string | undefined,
  hasAdditionalDisplayText: boolean,
) => {
  if (radioGroupPaddingTop) {
    return radioGroupPaddingTop;
  }
  if (hasAdditionalDisplayText) {
    return '0.5rem';
  }
  return '0';
};

export default function SimpleRadio({
  field,
  error,
  radioGroupPaddingTop,
  optionMarginRight = '1rem',
  ...question
}: Readonly<SimpleRadioProps>) {
  const { name, options, row = true } = question;
  const { setFocusedQuestion } = useQuestionLibraryActions();
  const { trigger } = useFormContext();

  const handleChange = (value: string) => {
    field.onChange(value);
    trigger(name);
  };

  const flexDirection = row ? ('row' as const) : ('column' as const);

  return (
    <StandardFormControl
      field={field}
      error={error}
      onFocus={() => setFocusedQuestion(question.name, question.smartAnswer)}
      padding={'0, 0.25rem, 1rem, 0.25rem'}
      {...question}
    >
      {question.additionalDisplayText && (
        <ConditionEvaluator
          conditions={question.additionalDisplayText.conditions}
        >
          <HelperText {...question.additionalDisplayText} padding={'0'} />
        </ConditionEvaluator>
      )}
      <RadioGroup
        paddingLeft={'0.25rem'}
        paddingTop={getRadioGroupPaddingTop(
          radioGroupPaddingTop,
          Boolean(question.additionalDisplayText),
        )}
        display={'flex'}
        flexDirection={flexDirection}
        value={field.value ?? ''}
        onChange={handleChange}
        aria-labelledby={`${name}-label`}
      >
        {options.map((option) => (
          <ConditionEvaluator
            conditions={option.conditions}
            key={`${name}-${option.value}`}
          >
            <RadioComponent
              key={option.value}
              name={name}
              value={option.value}
              isInvalid={error.hasError}
              marginRight={optionMarginRight}
              data-testid={`${name}-${option.value}`}
              sx={{
                position: 'relative',
              }}
            >
              <Text
                sx={{
                  position: 'relative',
                }}
              >
                {option.label}
                {option.infoToolTip && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -14,
                      right: -14,
                    }}
                  >
                    <GenericTooltip
                      placement="top"
                      label={option.infoToolTip}
                    />
                  </Box>
                )}
              </Text>
            </RadioComponent>
          </ConditionEvaluator>
        ))}
      </RadioGroup>
    </StandardFormControl>
  );
}
